import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { supabase } from './supabase';

interface WrappedResponse<T = any> {
  data: T;
  success: boolean;
  error: string | string[] | null;
}

class HttpClient {
  private client: AxiosInstance;

  constructor() {
    // Determine backend URL based on environment
    let backendURL = import.meta.env.VITE_BACKEND_URL;
    
    // If no env var is set, use intelligent defaults
    if (!backendURL) {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        // Development environment
        backendURL = 'http://localhost:8000/api/v1';
      } else {
        // Production environment - use same origin as frontend
        backendURL = '/api/v1';
      }
    }
    
    this.client = axios.create({
      baseURL: backendURL,
      timeout: 180000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.access_token) {
            config.headers.Authorization = `Bearer ${session.access_token}`;
          }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_error) {
          // Silently handle auth session errors
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Unwrap the middleware response format
        if (response.data && typeof response.data === 'object' && 
          'data' in response.data && 'success' in response.data && 'error' in response.data) {
          const wrapped = response.data as WrappedResponse<any>;
        
          // If success is false, treat it as an error
          if (!wrapped.success) {
            response.data = { detail: wrapped.error };
            const errorMessage = Array.isArray(wrapped.error) ? wrapped.error[0] : wrapped.error;
            const error: any = new Error(errorMessage || 'Request failed');
            error.response = response;
            error.config = response.config;
            error.request = response.request;
            error.isAxiosError = true;
            return Promise.reject(error);
          }

          // Return unwrapped data for successful responses
          return {
            ...response,
            data: wrapped.data
          };
        }
        
        // Return response as-is if not in wrapped format
        return response;
      },
      async (error) => {
        if (error.response?.status === 401) {
          // Token might be expired, try to refresh
          try {
            const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError || !session) {
              // Refresh failed, sign out and let React handle the state change
              await supabase.auth.signOut();
              // Don't redirect - let the AuthContext handle the state change
              return Promise.reject(error);
            }

            // Retry the original request with the new token
            const originalRequest = error.config;
            originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
            return this.client.request(originalRequest);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (_refreshError) {
            // Refresh failed, sign out and let React handle the state change
            await supabase.auth.signOut();
            // Don't redirect - let the AuthContext handle the state change
            return Promise.reject(error);
          }
        } else {
          if (error.response?.data && typeof error.response.data === 'object' &&
            'data' in error.response.data && 'success' in error.response.data && 'error' in error.response.data) {
            const wrapped = error.response.data as WrappedResponse<any>;
            
            error.message = wrapped.error || error.message;
            error.response.data = {
              detail: wrapped.error,
              error: wrapped.error,
              data: wrapped.data,
            };
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // HTTP methods
  async get<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config);
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config);
  }
}

export const httpClient = new HttpClient();